/**
 * RatingStars Component
 *
 * Display-only star rating component for recipes.
 * Shows filled/empty stars based on rating value (0-5).
 */

interface RatingStarsProps {
  rating: number | null | undefined;
  showValue?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

const sizeClasses = {
  sm: 'text-sm',
  md: 'text-base',
  lg: 'text-lg',
};

export function RatingStars({ rating, showValue = true, size = 'md' }: RatingStarsProps) {
  const displayRating = rating ?? 0;
  const fullStars = Math.floor(displayRating);
  const hasHalfStar = displayRating % 1 >= 0.5;

  return (
    <div className={`flex items-center gap-0.5 ${sizeClasses[size]}`}>
      {[1, 2, 3, 4, 5].map((star) => {
        const isFilled = star <= fullStars;
        const isHalf = star === fullStars + 1 && hasHalfStar;

        return (
          <span
            key={star}
            className={isFilled || isHalf ? 'text-amber-400' : 'text-gray-300'}
            aria-hidden="true"
          >
            {isHalf ? (
              // Half star using unicode
              <svg
                className={`inline-block ${size === 'sm' ? 'w-3 h-3' : size === 'lg' ? 'w-5 h-5' : 'w-4 h-4'}`}
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <defs>
                  <linearGradient id="halfStarGradient">
                    <stop offset="50%" stopColor="currentColor" />
                    <stop offset="50%" stopColor="#d1d5db" />
                  </linearGradient>
                </defs>
                <path
                  fill="url(#halfStarGradient)"
                  d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"
                />
              </svg>
            ) : (
              <svg
                className={`inline-block ${size === 'sm' ? 'w-3 h-3' : size === 'lg' ? 'w-5 h-5' : 'w-4 h-4'}`}
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
            )}
          </span>
        );
      })}
      {showValue && rating != null && (
        <span className="ml-1 text-gray-600">{rating.toFixed(1)}</span>
      )}
      {rating == null && showValue && (
        <span className="ml-1 text-gray-400 text-sm">No ratings</span>
      )}
    </div>
  );
}
